/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  const html = document.documentElement;
  const settings = window.AirXSettings || {};
  const intensity = settings.motionIntensity || 'full';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function revealEverything() {
    html.classList.remove('motion');
  }

  function init() {
    if (!window.gsap || reduced || intensity === 'off' || !html.classList.contains('motion')) {
      revealEverything();
      return;
    }

    const gsap = window.gsap;
    if (window.ScrollTrigger) gsap.registerPlugin(window.ScrollTrigger);
    if (window.SplitText) gsap.registerPlugin(window.SplitText);
    gsap.defaults({ ease: 'power3.out', duration: 0.9 });

    const full = intensity === 'full';
    const finePointer = window.matchMedia('(pointer: fine)').matches;

    const AirX = (window.AirX = window.AirX || {});
    AirX.motion = { scan, gsap };

    function animateElement(el) {
      const type = el.dataset.animate;
      const delay = parseFloat(el.dataset.animateDelay || 0);
      const trigger = { trigger: el, start: 'top 88%', once: true };

      switch (type) {
        case 'fade':
          gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.9, delay, scrollTrigger: trigger });
          break;
        case 'mask': {
          const img = el.querySelector('img, video');
          const tl = gsap.timeline({ scrollTrigger: trigger, delay });
          tl.fromTo(el, { clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)', duration: 1.1, ease: 'expo.out' });
          if (img) tl.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 1.4, ease: 'expo.out' }, 0);
          break;
        }
        case 'lines':
        case 'chars': {
          if (!window.SplitText) {
            gsap.fromTo(el, { opacity: 0, y: 24 }, { opacity: 1, y: 0, visibility: 'visible', delay, scrollTrigger: trigger });
            break;
          }
          document.fonts.ready.then(() => {
            try {
              const split = new window.SplitText(el, {
                type: type === 'lines' ? 'lines' : 'lines,chars',
                linesClass: 'split-line',
                mask: 'lines',
              });
              gsap.set(el, { visibility: 'visible' });
              const targets = type === 'lines' ? split.lines : split.chars;
              gsap.fromTo(
                targets,
                { yPercent: 110 },
                {
                  yPercent: 0,
                  duration: type === 'lines' ? 1.1 : 0.8,
                  ease: 'expo.out',
                  stagger: type === 'lines' ? 0.09 : 0.016,
                  delay,
                  scrollTrigger: trigger,
                  onComplete: () => split.revert(),
                }
              );
            } catch (e) {

              gsap.fromTo(el, { opacity: 0, y: 24 }, { opacity: 1, y: 0, visibility: 'visible', delay, scrollTrigger: trigger });
            }
          });
          break;
        }
        case 'rise':
        default:
          gsap.fromTo(el, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.9, delay, scrollTrigger: trigger });
      }
    }

    function animateGroup(group) {
      const children = Array.from(group.querySelectorAll(':scope [data-animate]')).filter((el) => {
        if (el.closest('[data-animate-group]') !== group) return false;

        return !['mask', 'lines', 'chars'].includes(el.dataset.animate);
      });
      if (!children.length) return;
      children.forEach((el) => (el.dataset.animateHandled = 'true'));
      gsap.fromTo(
        children,
        { opacity: 0, y: 28 },
        {
          opacity: 1, y: 0, duration: 0.9, stagger: 0.08,
          scrollTrigger: { trigger: group, start: 'top 85%', once: true },
        }
      );
    }

    function initParallax(el) {
      if (!full) { return; }
      const speed = parseFloat(el.dataset.parallax || 0.15);

      const distance = () => {
        const wanted = (el.offsetHeight + window.innerHeight) * speed;
        const parent = el.parentElement;
        if (!parent) return wanted;
        const bleed = el.offsetHeight - parent.offsetHeight - 2;
        return bleed > 0 ? Math.min(wanted, bleed) : wanted;
      };
      gsap.fromTo(
        el,
        { y: () => -distance() / 2 },
        {
          y: () => distance() / 2,
          ease: 'none',
          scrollTrigger: {
            trigger: el.parentElement || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.6,
            invalidateOnRefresh: true,
          },
        }
      );
    }

    function initMarquee(el) {
      const track = el.querySelector('[data-marquee-track]');
      if (!track) return;
      if (!full) {

        el.setAttribute('data-marquee-static', '');
        return;
      }
      const speed = parseFloat(el.dataset.marqueeSpeed || 60);
      const clone = () => {
        const n = track.children.length;
        if (!n) return 0;
        const width = track.scrollWidth;
        while (track.scrollWidth < window.innerWidth * 2 + width) {
          Array.from(track.children).slice(0, n).forEach((child) => {
            const copy = child.cloneNode(true);
            copy.setAttribute('aria-hidden', 'true');
            track.appendChild(copy);
          });
          if (track.children.length > 200) break;
        }

        if (!track.children[n]) return 0;
        return track.children[n].offsetLeft - track.children[0].offsetLeft;
      };
      const half = clone();
      if (!half) return;
      const duration = half / speed;
      const tween = gsap.to(track, { x: -half, duration, ease: 'none', repeat: -1 });
      if (el.dataset.marqueePauseHover !== 'false' && finePointer) {
        el.addEventListener('mouseenter', () => gsap.to(tween, { timeScale: 0.2, duration: 0.4 }));
        el.addEventListener('mouseleave', () => gsap.to(tween, { timeScale: 1, duration: 0.4 }));
      }
    }

    function initCounter(el) {
      const target = parseFloat(el.dataset.counter);
      if (isNaN(target)) return;
      const suffix = el.dataset.counterSuffix || '';
      const obj = { value: 0 };
      gsap.to(obj, {
        value: target,
        duration: 1.6,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        onUpdate: () => { el.textContent = Math.round(obj.value).toLocaleString() + suffix; },
      });
    }

    function initTilt(el) {
      if (!full || !finePointer || settings.cardTilt === false) return;
      const maxTilt = parseFloat(el.dataset.tiltMax || 6);
      const inner = el.querySelector('[data-tilt-inner]') || el;
      gsap.set(el, { perspective: 800 });
      gsap.set(inner, { transformStyle: 'preserve-3d', willChange: 'transform' });
      const qx = gsap.quickTo(inner, 'rotationX', { duration: 0.5, ease: 'power2.out' });
      const qy = gsap.quickTo(inner, 'rotationY', { duration: 0.5, ease: 'power2.out' });
      el.addEventListener('pointermove', (e) => {
        const rect = el.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        qx(-py * maxTilt);
        qy(px * maxTilt);
      });
      el.addEventListener('pointerleave', () => { qx(0); qy(0); });
    }

    function initMagnetic(el) {
      if (!full || !finePointer) return;
      const strength = parseFloat(el.dataset.magneticStrength || 0.3);
      const qx = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power2.out' });
      const qy = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power2.out' });
      el.addEventListener('pointermove', (e) => {
        const rect = el.getBoundingClientRect();
        qx((e.clientX - rect.left - rect.width / 2) * strength);
        qy((e.clientY - rect.top - rect.height / 2) * strength);
      });
      el.addEventListener('pointerleave', () => { qx(0); qy(0); });
    }

    function scan(root = document) {
      root.querySelectorAll('[data-animate-group]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        animateGroup(el);
      });
      root.querySelectorAll('[data-animate]:not([data-motion-init]):not([data-animate-handled])').forEach((el) => {
        el.dataset.motionInit = 'true';
        animateElement(el);
      });
      root.querySelectorAll('[data-parallax]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        initParallax(el);
      });
      root.querySelectorAll('[data-marquee]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        initMarquee(el);
      });
      root.querySelectorAll('[data-counter]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        initCounter(el);
      });
      root.querySelectorAll('[data-tilt]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        initTilt(el);
      });
      root.querySelectorAll('[data-magnetic]:not([data-motion-init])').forEach((el) => {
        el.dataset.motionInit = 'true';
        initMagnetic(el);
      });
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    }

    AirX.flyToCart = (sourceEl) => {
      if (!full || settings.cartFly === false || !sourceEl) return;
      const cartIcon = document.querySelector('[data-cart-icon]');
      if (!cartIcon) return;
      const from = sourceEl.getBoundingClientRect();
      const to = cartIcon.getBoundingClientRect();
      if (!from.width || !to.width) return;
      const ghost = sourceEl.cloneNode(true);
      Object.assign(ghost.style, {
        position: 'fixed',
        left: from.left + 'px',
        top: from.top + 'px',
        width: from.width + 'px',
        height: from.height + 'px',
        objectFit: 'cover',
        borderRadius: '8px',
        zIndex: 400,
        pointerEvents: 'none',
        margin: 0,
      });
      document.body.appendChild(ghost);
      gsap.timeline({ onComplete: () => ghost.remove() })
        .to(ghost, {
          left: to.left + to.width / 2 - 14,
          top: to.top + to.height / 2 - 14,
          width: 28,
          height: 28,
          borderRadius: '50%',
          opacity: 0.85,
          duration: 0.7,
          ease: 'power2.inOut',
        })
        .to(ghost, { opacity: 0, scale: 0.4, duration: 0.2 }, '-=0.05');
    };

    const veil = document.querySelector('.page-veil');
    if (veil && settings.pageTransitions !== false && full) {

      gsap.set(veil, { scaleY: 1, transformOrigin: 'top' });
      veil.classList.remove('is-entering');
      gsap.to(veil, { scaleY: 0, duration: 0.6, ease: 'power3.inOut', delay: 0.05 });

      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (!link) return;
        if (
          e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey ||
          link.target === '_blank' || link.hasAttribute('download') || link.hasAttribute('data-no-transition') ||
          link.hasAttribute('data-search-open') || link.hasAttribute('data-drawer-target') ||
          link.getAttribute('aria-haspopup') === 'dialog'
        ) return;
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname && url.hash) return;
        e.preventDefault();
        gsap.set(veil, { transformOrigin: 'bottom', scaleY: 0 });
        gsap.to(veil, {
          scaleY: 1, duration: 0.45, ease: 'power3.in',
          onComplete: () => { window.location.href = url.href; },
        });
      });

      window.addEventListener('pageshow', (e) => {
        if (e.persisted) gsap.set(veil, { scaleY: 0 });
      });
    } else if (veil) {
      veil.classList.remove('is-entering');
      veil.style.display = 'none';
    }

    scan();

    AirX.on('motion:scan', (e) => scan((e.detail && e.detail.root) || document));

    document.addEventListener('shopify:section:unload', (e) => {
      if (!window.ScrollTrigger) return;
      window.ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger && e.target.contains(st.trigger)) st.kill();
      });
    });
    document.addEventListener('shopify:section:load', (e) => scan(e.target));

    window.addEventListener('load', () => window.ScrollTrigger && window.ScrollTrigger.refresh());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
